import React, { useContext, useEffect, useState } from "react";
import PatientDetails from "../../Components/PatientInformation";
import SaveAndNavigate from "../../Components/SaveAndNavigate";
import {
  SideMenuItems,
  HTTPResponse,
  ToastMessageType,
  ToastMessages,
} from "../../Helpers/Enums";
import SideBarMenu from "../../Components/SideBarMenu";

import ErrorField from "../../Components/ErrorField";
import {
  CommonError_Msgs,
  EligibilityCriteria_Msgs,
  hyperlinkTextS11,
} from "../../Helpers/HelperText";
import InfoPopUp from "../../Components/InfoPopUp";
import { AppContext } from "../../Contexts/AppContextProvider";
import {
  GetLocalStorageData,
  GetLoggedInUserID,
  AddLocalStorageData,
  checkEligibileCohortTreatment,
  isStringEmpty,
  IsAdmin,
  IsLessOrEqual,
  CalculateAge,
  IsValidDateValue,
} from "../../Helpers/Utilities";
import dayjs from "dayjs";
import { EligibilityCriteriaService } from "../../WebApiServices/EligibilityCriteria.service";
import {
  COHORT_TREATMENT_DISPLAY_MAP,
  PageDesignConstants,
  RRMMCohortTreatment,
} from "../../Helpers/Enums";
import RRMMTreatmentCohortTable, {
  MakeEmptyRrmmRow,
  NOTA_VALUE,
} from "./CohortTreatmentCharacteristics/RRMMTreatmentCohortTable";

const RRMMCriteria = () => {
  const [isEligible, setIsEligible] = useState(false);
  const [isAllDataFilled, setIsAllDataFilled] = useState(false);

  const [isNewEcrfA, setIsNewEcrfA] = useState(false);

  const pid = GetLocalStorageData("patientId") ?? 0;
  let patientData = GetLocalStorageData("patient");
  patientData = patientData && JSON.parse(patientData);
  const [showHideHyperlinkModalS11, setShowHideHyperlinkModalS11] = useState(false);

  const shouldApplyNewCarvyktiRule =
    patientData?.shouldUseExpandedCarvyktiApproval ?? false;

  // Patients created on/after TecTalMutualExclusionCutoffDate (appsettings.json)
  // must never have both TECVAYLI and TALVEY rows, at any line of therapy -
  // broader than the existing same-LOT-only check below.
  const shouldValidateTecTalMutualExclusion =
    patientData?.shouldValidateTecTalMutualExclusion ?? false;

  const eligibilityCriteriaLocked =
    patientData?.eligibilityCriteriaLocked ?? false;

  const centerId = patientData?.ptCenterId ?? 0;
  const {
    ShowToast,
    ToggleLoader,
    ShowActionPopUp,
    HandleSessionTimeout,
    isSideMenuActive,
    setIsSideMenuActive,
    patientConfigData,
  } = useContext(AppContext);
  const terminatedPageId = patientConfigData?.data?.terminatedPageId;

  const HasEligibleNonNdmmNonMonoTherapyCohort = () => {
    const storedData = GetLocalStorageData("CohortTreatment");
    if (!storedData) return false;

    const cohortTreatmentList = JSON.parse(storedData);

    return cohortTreatmentList.some(
      (cohort) =>
        cohort.isEligible === true &&
        cohort.ndmm !== true &&
        cohort.monoTherapyReceived === false,
    );
  };

  const navigatePaths = {
    prevPage: "/NDMMCriteria",
    currPage: "/RRMMCriteria",
    nextPage:
      terminatedPageId === SideMenuItems.RRMMCriteria
        ? "/ManagePatients"
        : isNewEcrfA && HasEligibleNonNdmmNonMonoTherapyCohort()
          ? "/RRMMComboTherapy"
          : eligibilityCriteriaLocked
            ? "/ExclusionCriteria"
            : isAllDataFilled && isEligible
              ? "/ExclusionCriteria"
              : isAllDataFilled && !isEligible
                ? "/ManagePatients"
                : "/RRMMCriteria",
  };

  // RRMM_TREATMENT_CONFIG replaces the old fixed-per-drug S11_COHORT_CONFIG.
  // It is keyed by the RRMMCohortTreatment code (9-14, same as
  // COHORT_TREATMENT_DISPLAY_MAP / cohorttreatmentid on Q26) so a single
  // repeatable row's `treatment` value can look up everything it needs:
  // display label, the exact flat backend field names it maps to on
  // save/load (kept as a backward-compat mirror of the lowest-LOT row per
  // drug - see the comment at the SavePage mapping site below; the full set
  // of rows for every line of therapy persists via rrmmTreatmentReceivedList),
  // its FDA approval date used
  // for eligibility-date validation, whether it is a CAR-T therapy (which
  // always validates age + treats its date as an "index date"), and the
  // combination-therapy radio label text (which varies by drug).
  const RRMM_TREATMENT_CONFIG = {
    [RRMMCohortTreatment.TECVAYLI]: {
      backendKey: "Tecvayli",
      rbBackendField: "tecvayliReceived",
      displayLabel: COHORT_TREATMENT_DISPLAY_MAP[RRMMCohortTreatment.TECVAYLI],
      fdaApprovalDate: "2022/10/25",
      isCarT: false,
      comboLabel: "Received in combination with other agents (not Tal)",
    },
    [RRMMCohortTreatment.TALVEY]: {
      backendKey: "Talvey",
      rbBackendField: "telveyReceived",
      displayLabel: COHORT_TREATMENT_DISPLAY_MAP[RRMMCohortTreatment.TALVEY],
      fdaApprovalDate: "2023/08/09",
      isCarT: false,
      comboLabel: "Received in combination with other agents (not Tec)",
    },
    [RRMMCohortTreatment.ELREXFIO]: {
      backendKey: "Elrexfio",
      rbBackendField: "elrexfioReceived",
      displayLabel: COHORT_TREATMENT_DISPLAY_MAP[RRMMCohortTreatment.ELREXFIO],
      fdaApprovalDate: "2023/08/14",
      isCarT: false,
      comboLabel: "Received in combination with other agents",
    },
    [RRMMCohortTreatment.Linvoseltamab]: {
      backendKey: "Linvoseltamab",
      rbBackendField: "lynozyficReceived",
      displayLabel:
        COHORT_TREATMENT_DISPLAY_MAP[RRMMCohortTreatment.Linvoseltamab],
      fdaApprovalDate: "2024/09/20",
      isCarT: false,
      comboLabel: "Received in combination with other agents",
    },
    [RRMMCohortTreatment.CARVYKTI]: {
      backendKey: "Carvykti",
      rbBackendField: "carvyktiReceived",
      displayLabel: COHORT_TREATMENT_DISPLAY_MAP[RRMMCohortTreatment.CARVYKTI],
      fdaApprovalDate: shouldApplyNewCarvyktiRule ? "2024/04/05" : "2022/02/28",
      isCarT: true,
      comboLabel:
        "Received in combination with other agents (bridging therapy and lymphodepletion therapy are not considered part of combination therapy)",
    },
    [RRMMCohortTreatment.ABECMA]: {
      backendKey: "Abecma",
      rbBackendField: "abecmaReceived",
      displayLabel: COHORT_TREATMENT_DISPLAY_MAP[RRMMCohortTreatment.ABECMA],
      fdaApprovalDate: "2021/03/26",
      isCarT: true,
      comboLabel:
        "Received in combination with other agents (bridging therapy and lymphodepletion therapy are not considered part of combination therapy)",
    },
  };

  // TEC / TAL codes, used by the Tec+Tal mutual-exclusion hard error below.
  const TEC_CODE = RRMMCohortTreatment.TECVAYLI;
  const TAL_CODE = RRMMCohortTreatment.TALVEY;

  useEffect(() => {
    async function FetchData() {
      if (!isNaN(pid) && Number(pid) !== 0) {
        ToggleLoader(true);
        await LoadData();
        ToggleLoader(false);
      }
    }
    FetchData();
    // ShowPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [criteriaRRMM, setCriteriaRRMM] = useState({
    userId: GetLoggedInUserID(),

    //endofFollowUpDate: "",
    lastPatientEncounter: "",
    metInclusionCriteria: null,
    eligibilityDisabled: false,
    s4_birth_date: null,
    s4_MM_diagnosis_date: null,
    initiationDate1LTreatment: null,
  });

  // The repeatable S11/S12 rows. Each row = { rowId, treatment, monoCombo,
  // dateRadio, date, lot }. See RRMMTreatmentCohortTable.jsx / MakeEmptyRrmmRow.
  const [rrmmRows, setRrmmRows] = useState([MakeEmptyRrmmRow()]);

  const [showTopErrMsg, setShowTopErrMsg] = useState(false);

  // Errors (required fields, chronology, etc.) should stay invisible until
  // the abstractor has actually tried to save at least once - only then do
  // they live-recompute on every edit (see HandleRrmmRowsChange), so an
  // error like chronology persists correctly until fixed, without
  // prematurely flashing "required" errors on fields not yet filled in.
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const InitialHardErrList = {
    s11Empty: false,
    tecTalMutualExclusion: false,
    duplicateRow: false,
    chronology: false,
    byRow: {},
  };
  const [hardErrList, setHardErrList] = useState(InitialHardErrList);
  const InitialSoftErrList = {
    s11Terminate: false,
    initiationDateInvalid: false,
    patientAgeError: false,
    byRow: {},
  };
  const [softErrList, setSoftErrList] = useState(InitialSoftErrList);

  async function LoadData() {
    try {
      if (pid > 0) {
        // Fetch data from the service
        const response = await EligibilityCriteriaService.LoadRRMMCriteria(pid);

        if (response?.status === HTTPResponse.OK) {
          const data = response.data;

          setIsNewEcrfA(data.isNewEcrfA ?? false);

          // The Q26 (BispecificsCartTreatmentCohort) per-drug line-of-therapy
          // value is already returned on this same LoadRRMMCriteria response
          // as data.cohortTreatmentModelList (this is the same data the page
          // stores into localStorage under "CohortTreatment" for other
          // purposes below) - no new API call is needed to migrate column D.
          const cohortTreatmentModelList = data.cohortTreatmentModelList ?? [];
          const GetQ26LotForDrug = (code) => {
            const lots = cohortTreatmentModelList
              .filter(
                (c) =>
                  c.cohorttreatmentid === code &&
                  c.lOT !== null &&
                  c.lOT !== undefined &&
                  c.lOT !== "",
              )
              .map((c) => Number(c.lOT))
              .filter((n) => !isNaN(n));
            return lots.length > 0 ? Math.min(...lots) : "";
          };

          // Authoritative source: the backend now persists every S11/S12 row
          // (one per line of therapy) to Rrmmtreatmentreceived and returns
          // them here as rrmmTreatmentReceivedList (synthesizing them from
          // the old flat fields server-side the first time a patient is
          // loaded post-migration). Build rrmmRows directly from it,
          // preserving rrmmTreatmentReceivedId so edits round-trip back as
          // updates rather than inserts on save.
          const rrmmTreatmentReceivedList = data.rrmmTreatmentReceivedList ?? [];
          let nextRows = [];
          if (rrmmTreatmentReceivedList.length > 0) {
            // Map every saved row 1:1, whether it's NOTA or a real
            // treatment, preserving position - NOTA no longer has to be
            // row 0/the only row (it can coexist with other rows that were
            // entered before it was selected).
            nextRows = rrmmTreatmentReceivedList.map((row) =>
              row.cohortTreatmentNone === true
                ? {
                    ...MakeEmptyRrmmRow(),
                    rrmmTreatmentReceivedId: row.rrmmTreatmentReceivedId ?? null,
                    treatment: NOTA_VALUE,
                  }
                : {
                    ...MakeEmptyRrmmRow(),
                    rrmmTreatmentReceivedId: row.rrmmTreatmentReceivedId ?? null,
                    treatment: row.cohortTreatmentId ?? null,
                    monoCombo: row.receivedTherapyType ?? null,
                    dateRadio: row.indexDateUnknown
                      ? 99
                      : row.indexDate
                        ? 1
                        : null,
                    date: row.indexDate ?? "",
                    lot: row.lineOfTherapy ?? "",
                  },
            );
          } else {
            // Fallback only (defense in depth): the backend now migrates the
            // flat fields into rrmmTreatmentReceivedList authoritatively, so
            // this client-side migration should be a no-op in practice.
            if (data.cartTherapyNone === true) {
              nextRows = [{ ...MakeEmptyRrmmRow(), treatment: NOTA_VALUE }];
            } else {
              Object.keys(RRMM_TREATMENT_CONFIG).forEach((codeStr) => {
                const code = Number(codeStr);
                const cfg = RRMM_TREATMENT_CONFIG[code];
                const isChecked = data[`cartTherapy${cfg.backendKey}`];
                if (!isChecked) return;

                const indexDate = data[`indexdateCartTherapy${cfg.backendKey}`];
                const indexDateUnk =
                  data[`indexdateCartTherapy${cfg.backendKey}Unk`];

                nextRows.push({
                  ...MakeEmptyRrmmRow(),
                  treatment: code,
                  monoCombo: data[cfg.rbBackendField] ?? null,
                  dateRadio: indexDateUnk ? 99 : indexDate ? 1 : null,
                  date: indexDate ?? "",
                  lot: GetQ26LotForDrug(code),
                });
              });
            }
          }
          if (nextRows.length === 0) {
            nextRows = [MakeEmptyRrmmRow()];
          }
          setRrmmRows(nextRows);

          const nextCriteriaRRMM = {
            lastPatientEncounter: data.lastPatientEncounterDate,
            eligibilityDisabled: data.eligibilityLocked,
            s4_birth_date: !isStringEmpty(data.birthDate)
              ? data.birthDate
              : null,
            s4_MM_diagnosis_date: !isStringEmpty(data.mmDiagnosisDate)
              ? data.mmDiagnosisDate
              : null,
            initiationDate1LTreatment: data.initiationDate1LTreatment
              ? data.initiationDate1LTreatment
              : null,
          };
          setCriteriaRRMM((prev) => ({
            ...prev,
            ...nextCriteriaRRMM,
          }));

          if (data.cohortTreatmentModelList.length > 0)
            AddLocalStorageData(
              "CohortTreatment",
              JSON.stringify(data.cohortTreatmentModelList),
            );
          else AddLocalStorageData("CohortTreatment", JSON.stringify([]));

          // Compute cohort ineligibility for all rows on load
          const softErrUpdate = ComputeRRMMCohortSoftErrors(
            nextRows,
            nextCriteriaRRMM,
          );

          setSoftErrList((prev) => ({
            ...prev,
            ...softErrUpdate,
          }));
        } else if (response?.status === HTTPResponse.Unauthorized) {
          HandleSessionTimeout();
        }
      }
    } catch (error) {
      ShowToast(CommonError_Msgs.LOAD_ERR, ToastMessageType.Failed);
    }
  }

  // Generalizes the old fixed-per-drug ComputeRRMMCohortSoftErrors: iterates
  // the dynamic rows array instead of the fixed S11_COHORT_CONFIG object.
  const ComputeRRMMCohortSoftErrors = (rows, criteria) => {
    const byRow = {};
    let initiationDateInvalid = false;
    let patientAgeError = false;

    rows
      .filter((row) => typeof row.treatment === "number")
      .forEach((row) => {
        const cfg = RRMM_TREATMENT_CONFIG[row.treatment];
        const cohortDate = row.date;
        const unknownFlag = row.dateRadio === 99;
        let dateInvalid = false;
        if (unknownFlag === true) {
          dateInvalid = true;
        } else if (cohortDate) {
          dateInvalid = !IsCohortDateValid(
            criteria,
            cohortDate,
            cfg.fdaApprovalDate,
          );
        }

        let ageInvalid = false;
        const isMonotherapy = row.monoCombo === 1;
        const shouldValidateAge =
          cohortDate && (cfg.isCarT || isMonotherapy);

        if (shouldValidateAge) {
          ageInvalid = ValidatePatientAgeForCohort(criteria, cohortDate);
          if (ageInvalid) patientAgeError = true;
        }

        if (dateInvalid) initiationDateInvalid = true;

        byRow[row.rowId] = {
          dateInvalid,
          ageInvalid,
          terminate: dateInvalid || ageInvalid,
        };
      });

    return {
      byRow,
      // Row 0 = "None of the above treatments"
      s11Terminate: rows?.[0]?.treatment === NOTA_VALUE,
      initiationDateInvalid,
      patientAgeError,
    };
  };

  const UpdateRRMMSoftErrors = (rows, criteria = criteriaRRMM) => {
    const softErrUpdate = ComputeRRMMCohortSoftErrors(rows, criteria);
    setSoftErrList((prev) => ({
      ...prev,
      ...softErrUpdate,
    }));
  };

  // Called by RRMMTreatmentCohortTable with the full, already-updated rows
  // array whenever any field in any row changes (mirrors the
  // DoseFrequencyModificationTable.jsx pattern of the child owning row
  // editing and reporting the whole list back up). Once the abstractor has
  // attempted a save, hard errors (including chronology) are recomputed
  // live here rather than blindly cleared, so an error persists until the
  // field that actually caused it is fixed, not just any field. Before the
  // first save attempt, no errors are shown at all (matches the previous
  // behavior of not flashing "required" errors on untouched fields).
  const HandleRrmmRowsChange = (nextRows) => {
    setRrmmRows(nextRows);
    UpdateRRMMSoftErrors(nextRows);
    setHardErrList(
      hasAttemptedSave ? ComputeHardErrors(nextRows) : InitialHardErrList,
    );
  };

  const ValidatePatientAgeForCohort = (criteria, indexDate) => {
    if (!criteria.s4_birth_date || !indexDate) {
      return false;
    }

    const age = CalculateAge(criteria.s4_birth_date, indexDate);
    return age < 18;
  };

  const DateInRange = (date, minDate, maxDate) => {
    return dayjs(date).isAfter(minDate) && dayjs(date).isBefore(maxDate);
  };

  // Returns the list of rows that represent a real drug selection (i.e.
  // excludes NOTA rows and never-touched blank rows).
  const GetSelectedDrugRows = (rows) =>
    rows.filter((r) => typeof r.treatment === "number");

  // Pure computation of all hard errors for a given rows array - no state
  // mutation. Used both by ValidateForm (on Save) and by HandleRrmmRowsChange
  // (recomputed live on every edit) so an error like chronology persists
  // until the field that actually caused it is fixed, instead of being
  // blindly cleared whenever any unrelated field changes.
  const ComputeHardErrors = (rows) => {
    const byRow = {};
    const isNota = rows?.[0]?.treatment === NOTA_VALUE;
    const result = {
      s11Empty: false,
      tecTalMutualExclusion: false,
      duplicateRow: false,
      chronology: false,
    };

    // Mirrors the old S11 hard error: nothing selected at all and NOTA not
    // chosen either.
    if (!isNota && GetSelectedDrugRows(rows).length === 0) {
      result.s11Empty = true;
    }

    if (!isNota) {
      rows.forEach((row) => {
        if (row.treatment === NOTA_VALUE) {
          // NOTA selected in a row other than the first: a valid selection
          // on its own (no B/C/D fields to validate, rendered as "Not
          // applicable"), and does not trigger patient-ineligibility (that
          // only happens when row 0 is NOTA - see `isNota` above).
          return;
        }

        if (typeof row.treatment !== "number") {
          // A row was added but never given a treatment.
          byRow[row.rowId] = { ...byRow[row.rowId], treatmentMissing: true };
          return;
        }

        const rowErr = {};

        // Monotherapy vs combination required for all centers now (previously
        // only enforced for isNewEcrfA).
        if (row.monoCombo == null) {
          rowErr.rbMissing = true;
        }

        // Date radio (known/unknown) required.
        if (row.dateRadio == null) {
          rowErr.dateMissing = true;
        } else if (
          row.dateRadio === 1 &&
          (row.date === null || row.date === "")
        ) {
          rowErr.dateMissing = true;
        } else if (row.dateRadio === 1 && row.date) {
          if (row.date === "Invalid Date") {
            rowErr.dateInvalid = true;
          } else if (
            !DateInRange(row.date, dayjs("01-01-1920"), dayjs())
          ) {
            rowErr.dateInvalid = true;
          }
        }

        // Line of therapy required, integer, 2-40 inclusive.
        const lotStr = `${row.lot ?? ""}`.trim();
        const lotNum = Number(lotStr);
        if (
          isStringEmpty(lotStr) ||
          !Number.isInteger(lotNum) ||
          lotNum < 2 ||
          lotNum > 40
        ) {
          rowErr.lotRange = true;
        }

        if (Object.keys(rowErr).length > 0) {
          byRow[row.rowId] = { ...byRow[row.rowId], ...rowErr };
        }
      });

      const drugRows = GetSelectedDrugRows(rows);

      // Hard error: for patients created on/after TecTalMutualExclusionCutoffDate,
      // TEC and TAL can't both be selected for the same patient at all,
      // regardless of LOT.
      if (shouldValidateTecTalMutualExclusion) {
        const hasTec = drugRows.some((r) => r.treatment === TEC_CODE);
        const hasTal = drugRows.some((r) => r.treatment === TAL_CODE);
        if (hasTec && hasTal) {
          result.tecTalMutualExclusion = true;
        }
      }

      // Guard: duplicate rows (same drug + same LOT).
      const seen = new Set();
      drugRows.forEach((r) => {
        if (isStringEmpty(`${r.lot}`)) return;
        const key = `${r.treatment}-${Number(r.lot)}`;
        if (seen.has(key)) result.duplicateRow = true;
        seen.add(key);
      });

      // Hard error: cross-row chronological order, checked per cohort
      // (drug) - not across different drugs. Within each drug's own rows,
      // sort by numeric LOT ascending; each row's known initiation date
      // must be strictly before the next (higher LOT) row's known date for
      // that same drug.
      const rowsWithNumericLot = drugRows.filter(
        (r) => !isStringEmpty(`${r.lot}`) && !isNaN(Number(r.lot)),
      );
      const rowsByDrug = rowsWithNumericLot.reduce((acc, r) => {
        if (!acc[r.treatment]) acc[r.treatment] = [];
        acc[r.treatment].push(r);
        return acc;
      }, {});

      Object.values(rowsByDrug).forEach((sameDrugRows) => {
        const lotSortedRows = [...sameDrugRows].sort(
          (a, b) => Number(a.lot) - Number(b.lot),
        );
        for (let i = 0; i < lotSortedRows.length - 1; i++) {
          const cur = lotSortedRows[i];
          const next = lotSortedRows[i + 1];
          if (Number(cur.lot) === Number(next.lot)) continue; // handled by duplicateRow above
          if (
            cur.dateRadio === 1 &&
            cur.date &&
            next.dateRadio === 1 &&
            next.date &&
            !dayjs(cur.date).isBefore(dayjs(next.date))
          ) {
            result.chronology = true;
          }
        }
      });
    }

    return { ...result, byRow };
  };

  const IsHardErrValid = (computed) =>
    !computed.s11Empty &&
    !computed.tecTalMutualExclusion &&
    !computed.duplicateRow &&
    !computed.chronology &&
    Object.keys(computed.byRow).length === 0;

  const ValidateForm = () => {
    setHasAttemptedSave(true);
    const computed = ComputeHardErrors(rrmmRows);
    setHardErrList(computed);
    const isValid = IsHardErrValid(computed);
    setIsAllDataFilled(isValid);
    return isValid;
  };

  const SavePage = async (validate) => {
    try {
      setHardErrList(InitialHardErrList);
      ToggleLoader(true);

      let isValid = validate ? ValidateForm() : true;
      if (!isValid) {
        setShowTopErrMsg(true);
        ToggleLoader(false);
        return false;
      }
      setShowTopErrMsg(false);

      const storedData = GetLocalStorageData("CohortTreatment");

      let cohortTreatmentModelListt = [];
      if (storedData) {
        cohortTreatmentModelListt = JSON.parse(storedData);
      }

      const rows = rrmmRows;
      const isNota = rows?.[0]?.treatment === NOTA_VALUE;
      const drugRows = GetSelectedDrugRows(rows);

      // The full rrmmRows array (every row, every line of therapy) is what
      // actually persists now, via rrmmTreatmentReceivedList below - saved to
      // the Rrmmtreatmentreceived table, one row per line of therapy per
      // drug. The flat per-drug fields below are kept ONLY as an intentional
      // backward-compat mirror for other pages/services that still read them
      // (RRMMComboTherapy navigation, CommonService.GetCohortIndexDate, NDMM
      // eligibility checks): when a drug has multiple rows (different LOTs),
      // the flat fields reflect the row with the LOWEST line of therapy for
      // that drug (the earliest/primary line), falling back to the first row
      // for that drug if none of its rows have a usable numeric LOT.
      const GetLowestLotRowForDrug = (code) => {
        const rowsForDrug = drugRows.filter((r) => r.treatment === code);
        if (rowsForDrug.length === 0) return undefined;
        const withNumericLot = rowsForDrug.filter(
          (r) => !isStringEmpty(`${r.lot}`) && !isNaN(Number(r.lot)),
        );
        if (withNumericLot.length === 0) return rowsForDrug[0];
        return withNumericLot.reduce((lowest, r) =>
          Number(r.lot) < Number(lowest.lot) ? r : lowest,
        );
      };

      const perDrugFields = {};
      Object.keys(RRMM_TREATMENT_CONFIG).forEach((codeStr) => {
        const code = Number(codeStr);
        const cfg = RRMM_TREATMENT_CONFIG[code];
        const row = GetLowestLotRowForDrug(code);

        perDrugFields[`cartTherapy${cfg.backendKey}`] = row ? 1 : 0;
        perDrugFields[cfg.rbBackendField] = row ? row.monoCombo : null;
        perDrugFields[`indexdateCartTherapy${cfg.backendKey}`] = row
          ? row.date
          : "";
        perDrugFields[`indexdateCartTherapy${cfg.backendKey}Unk`] = row
          ? row.dateRadio === 99
          : false;
      });

      // Full row list -> Rrmmtreatmentreceived, every row persists (not just
      // one per drug) - including a NOTA row selected anywhere, not just row
      // 0 (GetSelectedDrugRows/drugRows deliberately excludes NOTA rows
      // since it's used for drug-only logic above; here we need every row).
      const rrmmTreatmentReceivedList = rows
        .filter(
          (r) => typeof r.treatment === "number" || r.treatment === NOTA_VALUE,
        )
        .map((row) =>
          row.treatment === NOTA_VALUE
            ? {
                rrmmTreatmentReceivedId: row.rrmmTreatmentReceivedId ?? null,
                cohortTreatmentId: null,
                cohortTreatmentNone: true,
                receivedTherapyType: null,
                indexDate: "",
                indexDateUnknown: false,
                lineOfTherapy: null,
              }
            : {
                rrmmTreatmentReceivedId: row.rrmmTreatmentReceivedId ?? null,
                cohortTreatmentId: row.treatment,
                cohortTreatmentNone: false,
                receivedTherapyType: row.monoCombo,
                indexDate: row.date || "",
                indexDateUnknown: row.dateRadio === 99,
                lineOfTherapy:
                  !isStringEmpty(`${row.lot}`) && !isNaN(Number(row.lot))
                    ? Number(row.lot)
                    : null,
              },
        );

      const softErrByRow = softErrList.byRow || {};
      const anyDrugSelected = drugRows.length > 0;
      const anyDrugNotTerminated = drugRows.some(
        (r) => !softErrByRow[r.rowId]?.terminate,
      );
      const allSelectedNotTerminated =
        drugRows.length > 0 &&
        drugRows.every((r) => !softErrByRow[r.rowId]?.terminate);
      const anySelectedTerminated = drugRows.some(
        (r) => softErrByRow[r.rowId]?.terminate,
      );
      const hasEligibleNdmmCohort =
        cohortTreatmentModelListt.filter((c) => c.ndmm === true).length >= 1;

      // Re-derives the old giant metInclusionCriteria boolean expression
      // from the rows array instead of the fixed S11_RRMM_* flags.
      const metInclusionCriteria =
        (!isNota && anyDrugSelected && anyDrugNotTerminated) ||
        (cohortTreatmentModelListt.length > 0 &&
          !isNota &&
          allSelectedNotTerminated) ||
        (!isNota && allSelectedNotTerminated) ||
        (isNota &&
          cohortTreatmentModelListt.length > 0 &&
          hasEligibleNdmmCohort &&
          softErrList.s11Terminate === true) ||
        (cohortTreatmentModelListt.length > 0 &&
          hasEligibleNdmmCohort &&
          !isNota &&
          anySelectedTerminated)
          ? true
          : false;

      const eligibilityCriteriaModel = {
        patientId: pid,
        centerId: centerId,
        userId: GetLoggedInUserID(),
        ...perDrugFields,
        cartTherapyNone: isNota ? 1 : null,
        cohortTreatmentModelList: [],
        rrmmTreatmentReceivedList: rrmmTreatmentReceivedList,
        metInclusionCriteria: metInclusionCriteria,
        isValidated: validate,
        IsAdmin: IsAdmin(),
        shouldUseExpandedCarvyktiApproval: shouldApplyNewCarvyktiRule,
      };
      setIsEligible(eligibilityCriteriaModel.metInclusionCriteria);

      setCriteriaRRMM((prev) => ({
        ...prev,
        metInclusionCriteria: eligibilityCriteriaModel.metInclusionCriteria,
      }));
      if (pid > 0) {
        const response = await EligibilityCriteriaService.SaveRRMMCriteria(
          eligibilityCriteriaModel,
        );

        if (response.status !== HTTPResponse.OK) {
          if (response?.status === HTTPResponse.Unauthorized) {
            HandleSessionTimeout();
          } else {
            throw response.error;
          }
        } else {
          setSoftErrList(InitialSoftErrList);
          ShowToast(ToastMessages.Save_Success, ToastMessageType.Success);
          await LoadData();
          ToggleLoader(false);
          if (response?.data?.showDateChangeAffectAlert) {
            ShowAlertBox();
          }
          if (eligibilityCriteriaModel.metInclusionCriteria === true) {
            if (isNewEcrfA && HasEligibleNonNdmmNonMonoTherapyCohort()) {
              // At least one cohort is received in combination
              navigatePaths.nextPage = "/RRMMComboTherapy";
            } else {
              navigatePaths.nextPage = "/ExclusionCriteria";
            }
          }
        }
      }
      return true;
      // }
    } catch (error) {
      ToggleLoader(false);
      ShowToast(CommonError_Msgs.SAVE_ERR, ToastMessageType.Failed);
    }
  };

  /* <summary>
      date: 24-08-2024
      Name: BV
      description: Show an alert message when other page(index date) could be affected
      <summary>*/
  const ShowAlertBox = async () => {
    await ShowActionPopUp(
      "Alert", //title
      CommonError_Msgs.DateChangeAlert, //message
      "Ok", //Confirm button text
      true,
    );
  };

  const IsCohortDateValid = (criteria, cohortDate, approvalDate) => {
    if (!cohortDate) return false;
    // FDA approval date check
    if (
      approvalDate &&
      checkEligibileCohortTreatment(cohortDate, approvalDate)
    ) {
      return false;
    }
    const { s4_birth_date, s4_MM_diagnosis_date, initiationDate1LTreatment } =
      criteria;

    // Date of birth ≤ date of initial MM diagnosis ≤ NDMM index date ≤ Bispecifics/CAR-T initiation date

    // DOB is mandatory
    if (!s4_birth_date) return false;

    const dateOfBirth = IsValidDateValue(s4_birth_date) ? s4_birth_date : null;
    const diagnosisDate = IsValidDateValue(s4_MM_diagnosis_date)
      ? s4_MM_diagnosis_date
      : null;
    const ndmmDate = IsValidDateValue(initiationDate1LTreatment)
      ? initiationDate1LTreatment
      : null;

    if (dateOfBirth && !IsLessOrEqual(dateOfBirth, cohortDate)) {
      return false;
    }

    if (diagnosisDate && !IsLessOrEqual(diagnosisDate, cohortDate)) {
      return false;
    }

    if (ndmmDate && !IsLessOrEqual(ndmmDate, cohortDate)) {
      return false;
    }

    return true;
  };

  const sidebar = document.getElementById("mySidebar");
  const main = document.getElementById("main");

  const toggleNav = () => {
    if (isSideMenuActive === true) {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
      setIsSideMenuActive(false);
    } else if (isSideMenuActive === false) {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
      setIsSideMenuActive(true);
    }
  };

  useEffect(() => {
    const sidebar = document.getElementById("mySidebar");
    const main = document.getElementById("main");

    if (isSideMenuActive === true) {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
    } else {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
    }
  });
  return (
    <>
      <section className="survey-patient-details-section">
        <div className="row">
          <PatientDetails />
          <div className="col-12 col-sm-5 col-md-5 col-lg-6 col-content-alignment">
            <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
          </div>
        </div>
      </section>
      <section className="main-survey-section">
        <div>
          <SideBarMenu activePageId={SideMenuItems.RRMMCriteria} />
          <>
            <button
              className={isSideMenuActive ? "openbtn" : "openbtn active"}
              // onclick="toggleNav()"
              onClick={() => toggleNav()}
            >
              <i
                id="arrowNavigation"
                className="fa fa-arrow-left"
                aria-hidden="true"
              ></i>
            </button>
          </>

          <div className="col-pad-left eligibiltylocked" id="main">
            <div className="survey-section-content">
              <div className="survey-section-content-header">
                <h4>RRMM CRITERIA</h4>
              </div>
              {!showTopErrMsg &&
                Number(pid) !== 0 &&
                criteriaRRMM.metInclusionCriteria != null &&
                criteriaRRMM.metInclusionCriteria === false && (
                  <div className="clean-error">
                    <p>{EligibilityCriteria_Msgs.IneligiblePatient}</p>
                  </div>
                )}

              {showTopErrMsg && (
                <div className="clean-error ">
                  <ErrorField
                    show={showTopErrMsg}
                    message={CommonError_Msgs.Hard_Empty}
                  />
                </div>
              )}
              <div
                className="survey-question-section"
                disabled={criteriaRRMM.eligibilityDisabled}
              >
                <div className="survey-question-content">
                  <div className="question-answer">
                    <div className="question-bot-sapce">
                      <div className="question question-weight">
                        <span>S11.1</span>
                        <span className="quest-text-pad">
                          <span> Which, if any, of the following</span>{" "}
                          <span
                            data-tooltip-id="tooltip-hyperlink-s11"
                            className="modal-popup"
                            onClick={() => setShowHideHyperlinkModalS11(true)}
                          >
                            bispecific or CAR-T therapies
                            </span>
                            {showHideHyperlinkModalS11 && (
                            <InfoPopUp
                              description={hyperlinkTextS11}
                              OkClick={() => setShowHideHyperlinkModalS11(false)}
                            />
                            )}{" "}
                          did this patient receive for the treatment of MM{" "}
                          <b>at your medical center</b>{" "}
                          <u>
                            <b>
                              in any subsequent lines of therapy (any line
                              except 1L)
                            </b>
                          </u>
                          ? Please note that:
                          <br />
                          <span style={{ padding: "10px 0" }}>
                            Treatments can be received as monotherapy or as part
                            of a combination therapy.
                          </span>
                          <br />
                          <div className="mt-1 mb-1">
                            For Bispecific therapies:
                            <ul className="mt-2">
                              <li>
                                Patients{" "}
                                <u>
                                  <b>do not have</b>
                                </u>{" "}
                                to have completed the entire line of therapy to
                                be eligible (i.e., the treatment regimen can be
                                ongoing by the time of data abstraction or last
                                visit)
                              </li>
                              <li>
                                Patients{" "}
                                <u>
                                  <b>are not permitted</b>
                                </u>{" "}
                                to have received any part of therapy as part of
                                a clinical trial or expanded access program
                                (EAP)
                              </li>
                            </ul>
                          </div>
                          <div>
                            For CAR-T therapies:
                            <ul className="mt-2 mb-1">
                              <li>
                                Patients{" "}
                                <u>
                                  <b>are required</b>
                                </u>{" "}
                                to have received the CAR-T infusion.
                              </li>
                              <li>
                                Patients{" "}
                                <u>
                                  <b>are permitted</b>
                                </u>{" "}
                                to receive any part of the therapy as part of an
                                EAP but{" "}
                                <u>
                                  <b>are not permitted</b>
                                </u>{" "}
                                to receive any part of the therapy in a clinical
                                trial
                              </li>
                            </ul>
                          </div>
                          <span>
                            If a Bispecific therapy was used as bridging therapy
                            for CAR-T, this{" "}
                            <u>
                              <b>is not considered</b>
                            </u>{" "}
                            combination therapy. Please abstract this as a{" "}
                            <u>
                              <b>line of CAR-T therapy only</b>
                            </u>
                            ; the bispecific therapy should not be abstracted as
                            its own cohort nor as a combination agent for the
                            CAR-T therapy.
                          </span>
                          <div
                            className="mt-2"
                            style={{ color: PageDesignConstants.fontColor }}
                          >
                            <span>
                              <u>
                                AS OF 9/1/2026 THE FOLLOWING IMPORTANT UPDATES
                                APPLY:
                              </u>
                              <ul>
                                <li>
                                  All 5L+ cohort regimens have been retired.
                                  Abstraction should be limited to patients
                                  who initiated Bispecific/CAR-T treatment
                                  within 2-4L only.
                                </li>
                                <li>
                                  eMMpower will now allow for the abstraction
                                  of multiple lines of therapy where the same
                                  Bispecific/CAR-T agent was received (e.g.,
                                  2L Tec, 3L Tec, 4L Tec combo can now all be
                                  abstracted).
                                  <ul>
                                    <li>
                                      Please add a new row for each unique
                                      line of therapy (LOT) with
                                      Bispecific/CAR-T treatment.
                                    </li>
                                  </ul>
                                </li>
                                <li>
                                  Patients receiving Teclistimab + Talquetamab
                                  in the same LOT will no longer be considered
                                  eligible for abstraction within the study.
                                  Validation logic is now in place to prevent
                                  abstraction of these patients.
                                </li>
                              </ul>
                            </span>
                          </div>
                        </span>
                      </div>
                      <div className="double-dig-answer mt-2">
                        <ErrorField
                          show={hardErrList.s11Empty}
                          message={EligibilityCriteria_Msgs.S11}
                        />
                        <RRMMTreatmentCohortTable
                          rows={rrmmRows}
                          treatmentConfig={RRMM_TREATMENT_CONFIG}
                          hardErrByRow={hardErrList.byRow}
                          softErrByRow={softErrList.byRow}
                          onRowsChange={HandleRrmmRowsChange}
                          disabled={criteriaRRMM.eligibilityDisabled}
                        />
                        <ErrorField
                          style={{ marginTop: "10px" }}
                          show={hardErrList.tecTalMutualExclusion}
                          message={
                            EligibilityCriteria_Msgs.TecTalMutualExclusionError
                          }
                        />
                        <ErrorField
                          style={{ marginTop: "10px" }}
                          show={hardErrList.duplicateRow}
                          message={EligibilityCriteria_Msgs.RRMMDuplicateRowError}
                        />
                        <ErrorField
                          style={{ marginTop: "10px" }}
                          show={hardErrList.chronology}
                          message={EligibilityCriteria_Msgs.RRMMRowChronologyError}
                        />
                        <ErrorField
                          show={softErrList.s11Terminate}
                          message={EligibilityCriteria_Msgs.INELIGIBLE}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="survey-section-content-foot">
                <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RRMMCriteria;
